import React, { useState } from "react"
import { css } from "@emotion/core"
import axios from "axios"
import { useFormInput } from "../hooks/useFormInput"
import { useWindowWidth } from "../hooks/useWindowWidth"

import {
  Input,
  Textarea,
  File,
  GradientButton,
  Dropdown,
  Spinner,
  GradientCircleImage,
} from "./common"

import TickImage from "../images/common/tick.svg"

const JobForm = () => {
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const name = useFormInput("")
  const email = useFormInput("")
  const skills = useFormInput("")
  const level = useFormInput("")
  const portfolio = useFormInput("")
  const about = useFormInput("")
  const [file, setFile] = useState()
  const [progress, setProgress] = useState(0)
  const [fileName, setFileName] = useState()

  const width = useWindowWidth()

  const clearState = () => {
    name.onChange({ target: { value: "" } })
    email.onChange({ target: { value: "" } })
    skills.onChange({ target: { value: "" } })
    level.onChange({ target: { value: "" } })
    portfolio.onChange({ target: { value: "" } })
    about.onChange({ target: { value: "" } })
    setFile()
    setProgress(0)
    setFileName()
  }

  const submitData = async () => {
    setSubmitting(true)
    const formData = new FormData()
    formData.append("name", name.value)
    formData.append("email", email.value)
    formData.append("skills", skills.value)
    formData.append("level", level.value)
    formData.append("portfolio", portfolio.value)
    formData.append("about", about.value)
    formData.append("cv", file)
    try {
      const response = await axios.post(
        `${process.env.GATSBY_BACKEND_URL}/career`,
        formData,
        {
          onUploadProgress: event => {
            if (event.lengthComputable) {
              setProgress((event.loaded / event.total) * 100)
            }
          },
        }
      )
      if (response.data.response === "ok") {
        clearState()
        setSubmitting(false)
        setSubmitted(true)
      } else {
        throw new Error("Couldn't upload")
      }
    } catch (err) {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div
        css={theme => css`
          color: ${theme.colors.white};
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;

          padding: 60px;
          height: 610px;

          @media (max-width: 1200px) {
            height: 880px;
          }

          @media (max-width: 880px) {
            padding: 20px;
            height: 830px;
          }
        `}
      >
        <GradientCircleImage
          fillColor="white"
          border={2}
          size={120}
          src={TickImage}
        />
        <div
          css={theme => css`
            font-size: ${theme.constants.mediumFontSize}px;
            font-weight: ${theme.constants.demi};
            color: ${theme.colors.black};
            margin-top: 60px;

            @media (max-width: 600px) {
              font-size: ${theme.constants.smallFontSize}px;
            }
          `}
        >
          Thank you!
        </div>
        <div
          css={theme => css`
            font-size: ${theme.constants.contactBoxDescriptionSize}px;
            color: ${theme.colors.darkGray};

            margin-top: 30px;
            margin-bottom: 60px;

            @media (max-width: 600px) {
              font-size: ${theme.constants.smallFontSize}px;
            }
          `}
        >
          Your message has been sent. successfully. We will reply in 5 to 10
          business days.
        </div>
        <button
          css={theme => css`
            color: ${theme.colors.blue};
            font-size: ${theme.constants.extraSmallFontSize}px;
            font-weight: ${theme.constants.medium};
            background: transparent;
            cursor: pointer;
          `}
          onClick={() => setSubmitted(false)}
        >
          Send new message
        </button>
      </div>
    )
  }

  if (submitting) {
    return (
      <div
        css={theme => css`
          color: ${theme.colors.white};
          height: 610px;
          padding: 60px;

          @media (max-width: 1200px) {
            height: 880px;
          }

          @media (max-width: 880px) {
            padding: 20px;
            height: 830px;
          }
        `}
      >
        <Spinner fillColor="white" size={150} progress={progress} />
      </div>
    )
  }

  return (
    <div
      css={theme => css`
        color: ${theme.colors.white};
        padding: 60px;

        @media (max-width: 880px) {
          padding: 20px;
        }
      `}
    >
      <div
        css={theme => css`
          font-size: ${theme.constants.mediumFontSize}px;
          font-weight: ${theme.constants.demi};
          margin-bottom: 60px;
          color: ${theme.colors.black};

          @media (max-width: 880px) {
            margin-top: 30px;
          }
        `}
      >
        Interested?
      </div>
      <form
        css={css`
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(42%, 1fr));
          width: 100%;
          grid-column-gap: 60px;

          @media (max-width: 1350px) {
            display: flex;
            flex-direction: column;
          }
        `}
        onSubmit={e => e.preventDefault()}
      >
        <Input light name="name" {...name} placeholder="Full name" />
        <Input
          light
          name="email"
          type="email"
          {...email}
          placeholder="Your mail address"
        />
        <Input
          light
          name="skills"
          {...skills}
          placeholder="Android, iOS, Backend..."
        />
        <Dropdown
          light
          name="level"
          {...level}
          placeholder="Level - Junior, medior, senior"
          options={["Junior", "Medior", "Senior"]}
        />
        <Input
          light
          name="portfolio"
          {...portfolio}
          placeholder="Portfolio, Github url"
        />
        <File
          light
          name="cv"
          fileName={fileName}
          onName={setFileName}
          onFile={setFile}
          placeholder="Attach your cv"
        />
        <Textarea
          lines={5}
          light
          name="about"
          {...about}
          placeholder="Please tell us a bit about yourself..."
        />
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            width: 100%;
            grid-column-start: 2;
            align-items: flex-end;
          `}
        >
          <GradientButton
            disabled={
              !name.value ||
              !email.value ||
              !skills.value ||
              !level.value ||
              !portfolio.value ||
              !about.value ||
              !file
            }
            onClick={() => submitData()}
            centered={width <= 880}
            css={css`
              width: 280px;

              @media (max-width: 880px) {
                width: 100%;
              }
            `}
          >
            submit application
          </GradientButton>
        </div>
      </form>
    </div>
  )
}

export default JobForm
